/**
 * @file blendReferenceData.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-08-24
 * @description Customers and contract periods for the blend screens.
 *
 *              Both the calculator's details panel and the export modal need the
 *              same two lists, and the customer list is ten thousand rows — so
 *              they share one cached promise rather than each fetching it. Two
 *              callers in quick succession share a single request instead of
 *              racing.
 *
 *              A failed fetch clears the cache so the next call retries; caching
 *              a rejection would leave the dropdowns permanently empty after one
 *              blip.
 */

import { API_URL } from '../config';

let cache = null;

export function blendReferenceData() {
  if (cache) return cache;
  cache = Promise.all([
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`,
          { credentials: 'include' }).then((r) => (r.ok ? r.json() : { contacts: [] })),
    // Scoped to blends. A period belongs to a product category — "Summer 2026"
    // exists separately for straights, blends, compounds and bags — and asking
    // for all of them is what made this dropdown show the same name three
    // times. By name rather than by id so there is no magic number here.
    fetch(`${API_URL}/api/pricing/periods?category=blends&is_active=all`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
  ]).then(([c, p]) => ({
    customers: c.contacts || c.data || [],
    periods: Array.isArray(p) ? p : []
  })).catch((e) => { cache = null; throw e; });
  return cache;
}

/**
 * Contract periods labelled by where they stand, newest-relevant first.
 *
 * NOT de-duplicated by name. It was, and that was a mistake built on a second
 * mistake: the repeated "Summer 2026" this list used to show was four real
 * periods belonging to four product categories, not one period stored four
 * times. Deduping by name kept whichever happened to sort first — often the
 * straights row, which is inactive — and dropped the blends one entirely.
 * The fetch is scoped to a category instead, so there is nothing to dedupe.
 *
 * "Current" is the period we are inside, not merely the last to have started —
 * those differ once a period ends before the next begins, and the latter would
 * label an expired period as current.
 */
export function periodOptionsFrom(periods, { includeCustom = false } = {}) {
  const now = new Date();
  const sorted = (periods || [])
    .filter((p) => p.start_date)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  let cur = sorted.findIndex(
    (p) => new Date(p.start_date) <= now && (!p.end_date || new Date(p.end_date) >= now));
  if (cur === -1) sorted.forEach((p, i) => { if (new Date(p.start_date) <= now) cur = i; });

  const out = [{ value: '', label: 'Not set' }];
  sorted.forEach((p, i) => {
    if (i < cur - 1 || i > cur + 2) return;
    const rel = i === cur ? ' (current)'
      : i < cur ? ' (previous)'
      : i === cur + 1 ? ' (next)' : ' (future)';
    out.push({ value: p.name, label: `${p.name}${rel}` });
  });

  // Custom dates are the escape hatch for a contract that doesn't sit on a
  // season boundary.
  if (includeCustom) out.push({ value: 'custom', label: 'Custom dates…' });
  return out;
}
